import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { ArrowLeft, ArrowRight, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/orgApi";
import { useAuth } from "@/context/AuthContext";

const ORG_LOGO = `${process.env.PUBLIC_URL}/organization-logo.webp`;

export default function SelectContextPage() {
  const { user, setContext, logout } = useAuth();
  const nav = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [locs, setLocs] = useState([]);
  const [orgId, setOrgId] = useState("");
  const [locId, setLocId] = useState("");
  const [busy, setBusy] = useState(false);

  const orgWide = !!user?.orgWide;
  const allowedLocIds = useMemo(
    () => new Set((user?.locationAccess || []).map((la) => (la.location?.reference || "").split("/")[1])),
    [user]
  );

  useEffect(() => {
    Promise.all([api.get("/organizations"), api.get("/locations")]).then(([o, l]) => {
      const userOrgIds = new Set(user?.organizationIds || []);
      const myOrgs = orgWide ? o.data.items : (o.data.items || []).filter((x) => userOrgIds.has(x.id));
      setOrgs(myOrgs);
      setLocs(l.data.items || []);
      const defOrg = (user?.primaryOrganization?.reference || "").split("/")[1] || myOrgs[0]?.id || "";
      setOrgId(defOrg);
    });
  }, [user, orgWide]);

  const locsForOrg = useMemo(() => {
    return locs.filter((l) => {
      const belongs = (l.managingOrganization?.reference || "").split("/")[1] === orgId;
      const authorized = orgWide || allowedLocIds.has(l.id);
      return belongs && authorized;
    });
  }, [locs, orgId, orgWide, allowedLocIds]);

  useEffect(() => {
    if (!orgId) { setLocId(""); return; }
    const def = (user?.locationAccess || []).find((la) => la.isDefault);
    const defId = (def?.location?.reference || "").split("/")[1];
    const preferred = defId && locsForOrg.some((l) => l.id === defId) ? defId : locsForOrg[0]?.id || "";
    setLocId(preferred);
  }, [orgId, locsForOrg, user]);

  const continueIn = async () => {
    if (!orgId || !locId) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/select-context", { organizationId: orgId, locationId: locId });
      setContext({ organization: data.organization, location: data.location });
      nav("/");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid working context");
    } finally { setBusy(false); }
  };

  const back = async () => { await logout(); nav("/login"); };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-100 px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-6 flex flex-col items-center">
          <span className="mb-6 rounded-full bg-teal-700 px-4 py-1 text-xs font-semibold tracking-widest text-white">STEP 2 · WORKING LOCATION</span>
          <img src={ORG_LOGO} alt="New York Pain Specialist PLLC" className="h-16 w-auto rounded" />
          <h2 className="mt-3 text-center text-lg font-semibold text-slate-900">New York Pain Specialist PLLC</h2>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-xl" data-testid="select-context-page">
          <div className="mb-6 text-center">
            <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-teal-50 text-teal-700">
              <MapPin className="h-6 w-6" />
            </div>
            <h1 className="text-page-title font-semibold text-slate-900">Choose your workspace</h1>
            <p className="text-sm text-slate-500">Select the organization and location you're working in.</p>
          </div>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-sm font-semibold text-slate-700">Organization</Label>
              <Select value={orgId} onValueChange={setOrgId}>
                <SelectTrigger data-testid="context-org-select"><SelectValue placeholder="Select organization" /></SelectTrigger>
                <SelectContent>{orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-semibold text-slate-700">Location</Label>
              <Select value={locId} onValueChange={setLocId} disabled={!orgId}>
                <SelectTrigger data-testid="context-location-select"><SelectValue placeholder="Select location" /></SelectTrigger>
                <SelectContent>{locsForOrg.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between pt-2">
              <Button variant="ghost" onClick={back} data-testid="context-back" className="text-slate-600">
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              <Button onClick={continueIn} disabled={!orgId || !locId || busy} data-testid="context-continue"
                className="bg-teal-700 hover:bg-teal-800">
                {busy ? "Entering…" : "Continue"} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
